import * as Yup from 'yup';

import type { YesNoBooleanFormValue } from '@customTypes/form';
import { OptionalYesNoBooleanValidator } from '@validators/strings';

const ELIGIBILITY_ERROR = 'Confirm this to submit the referral.';

export interface HcpReferralEligibilityFormValues {
  is_us_resident: boolean;
  has_detected_variant: boolean;
}

export interface HcpReferralClinicalHistoryFormValues {
  low_blood_calcium: YesNoBooleanFormValue;
  elevated_urine_calcium: YesNoBooleanFormValue;
  adh1_symptoms: YesNoBooleanFormValue;
  adh1_diagnosis: YesNoBooleanFormValue;
}

export interface HcpReferralFormValues {
  patient_first_name: string;
  patient_last_name: string;
  patient_email: string;
  hcp_first_name: string;
  hcp_last_name: string;
  hcp_email: string;
  eligibility: HcpReferralEligibilityFormValues;
  clinical_history: HcpReferralClinicalHistoryFormValues;
  report_pdf: File | null;
}

export const referralEligibilityFormSchema = Yup.object({
  has_detected_variant: Yup.boolean().oneOf([true], ELIGIBILITY_ERROR).required(ELIGIBILITY_ERROR),
  is_us_resident: Yup.boolean().oneOf([true], ELIGIBILITY_ERROR).required(ELIGIBILITY_ERROR),
});

export const referralClinicalHistoryFormSchema = Yup.object({
  adh1_diagnosis: OptionalYesNoBooleanValidator(),
  adh1_symptoms: OptionalYesNoBooleanValidator(),
  elevated_urine_calcium: OptionalYesNoBooleanValidator(),
  low_blood_calcium: OptionalYesNoBooleanValidator(),
});

// File-type/size validation happens inside FileUpload (Yup is awkward at validating a
// File cross-field with a custom dropzone UI). The schema only enforces presence.
export const hcpReferralSchema: Yup.ObjectSchema<HcpReferralFormValues> = Yup.object({
  patient_first_name: Yup.string().trim().default(''),
  patient_last_name: Yup.string().trim().default(''),
  patient_email: Yup.string().trim().email('Enter a valid email address.').required('Patient email is required.'),
  report_pdf: Yup.mixed<File>()
    .required('A report PDF is required to submit.')
    .test('is-file', 'A report PDF is required to submit.', value => value instanceof File),
  hcp_first_name: Yup.string().trim().default(''),
  hcp_last_name: Yup.string().trim().default(''),
  // Optional: if provided it must be a valid email, but HCPs may submit without one.
  hcp_email: Yup.string().trim().email('Enter a valid email address.').default(''),
  clinical_history: referralClinicalHistoryFormSchema.required('Clinical history is required'),
  eligibility: referralEligibilityFormSchema.required('Eligibility is required'),
});

export const getInitialValues = (): HcpReferralFormValues => ({
  patient_first_name: '',
  patient_last_name: '',
  patient_email: '',
  hcp_first_name: '',
  hcp_last_name: '',
  hcp_email: '',
  report_pdf: null,
  eligibility: {
    is_us_resident: false,
    has_detected_variant: false,
  },
  clinical_history: {
    low_blood_calcium: '',
    elevated_urine_calcium: '',
    adh1_symptoms: '',
    adh1_diagnosis: '',
  },
});
